import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeftRight,
  Plus,
  Loader2,
  Calendar,
  Users,
  Target,
  Clock,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import {
  getRedeploymentReasons,
  createRedeployment,
  getRedeploymentPlans
} from '../../services/api';
import { toast } from 'sonner';

const Redeployment = () => {
  const [reasons, setReasons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    affected_positions: [],
    target_date: '',
    budget: '',
    notes: ''
  });
  const [newPosition, setNewPosition] = useState({ title: '', count: 1, department: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reasonsRes, plansRes] = await Promise.all([
        getRedeploymentReasons(),
        getRedeploymentPlans()
      ]);
      setReasons(reasonsRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addPosition = () => {
    if (!newPosition.title) return;
    setFormData({
      ...formData,
      affected_positions: [...formData.affected_positions, { ...newPosition }]
    });
    setNewPosition({ title: '', count: 1, department: '' });
  };

  const removePosition = (idx) => {
    setFormData({
      ...formData,
      affected_positions: formData.affected_positions.filter((_, i) => i !== idx)
    });
  };

  const handleCreate = async () => {
    if (!formData.reason) {
      toast.error('Bitte wählen Sie einen Grund');
      return;
    }
    setCreating(true);
    try {
      const res = await createRedeployment(formData);
      setPlans([res.data, ...plans]);
      setShowCreate(false);
      setFormData({ reason: '', affected_positions: [], target_date: '', budget: '', notes: '' });
      toast.success('Redeployment-Plan erstellt');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || colors.planning;
  };

  const getStatusLabel = (status) => {
    const labels = { planning: 'In Planung', active: 'Aktiv', completed: 'Abgeschlossen' };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="redeployment-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
              <ArrowLeftRight className="w-8 h-8 text-primary" />
              Redeployment
            </h1>
            <p className="text-muted-foreground mt-1">
              Interne Mobilität und Restrukturierung verwalten
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="rounded-full" data-testid="create-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Plan
          </Button>
        </div>

        {/* Create Form */}
        {showCreate && (
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle>Neuen Redeployment-Plan erstellen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Grund der Maßnahme *</Label>
                <Select value={formData.reason} onValueChange={(v) => setFormData({ ...formData, reason: v })}>
                  <SelectTrigger data-testid="reason-select">
                    <SelectValue placeholder="Grund wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map(r => (
                      <SelectItem key={r.key} value={r.key}>{r.label} - {r.desc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Zieldatum</Label>
                  <Input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Budget</Label>
                  <Input
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="z.B. 50.000 EUR"
                  />
                </div>
              </div>

              {/* Affected Positions */}
              <div className="space-y-3">
                <Label>Betroffene Positionen</Label>
                {formData.affected_positions.map((pos, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <span className="flex-1 font-medium text-sm">{pos.title}</span>
                    <Badge variant="secondary">{pos.count}x</Badge>
                    <span className="text-sm text-muted-foreground">{pos.department}</span>
                    <Button variant="ghost" size="sm" onClick={() => removePosition(idx)}>
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input
                    value={newPosition.title}
                    onChange={(e) => setNewPosition({ ...newPosition, title: e.target.value })}
                    placeholder="Position..."
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={newPosition.count}
                    onChange={(e) => setNewPosition({ ...newPosition, count: parseInt(e.target.value) || 1 })}
                    className="w-20"
                    min={1}
                  />
                  <Input
                    value={newPosition.department}
                    onChange={(e) => setNewPosition({ ...newPosition, department: e.target.value })}
                    placeholder="Abteilung"
                    className="w-40"
                  />
                  <Button variant="outline" onClick={addPosition}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Anmerkungen</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Weitere Details..."
                  rows={3}
                />
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">
                  Abbrechen
                </Button>
                <Button onClick={handleCreate} disabled={creating || !formData.reason} className="rounded-full flex-1" data-testid="submit-plan-btn">
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Plan erstellen
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans List */}
        {plans.length > 0 ? (
          <div className="grid gap-4">
            {plans.map((plan) => (
              <Card key={plan.redeployment_id} className="hover:shadow-md transition-all" data-testid={`plan-${plan.redeployment_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">
                          {reasons.find(r => r.key === plan.reason)?.label || plan.reason}
                        </h3>
                        <Badge className={getStatusColor(plan.status)}>
                          {getStatusLabel(plan.status)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        {plan.target_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(plan.target_date).toLocaleDateString('de-DE')}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {plan.affected_positions?.length || 0} Positionen
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(plan.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      {plan.affected_positions?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {plan.affected_positions.map((pos, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {pos.title} ({pos.count}x)
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : !showCreate && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <ArrowLeftRight className="w-16 h-16 mx-auto text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Noch keine Pläne</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                Erstellen Sie einen Redeployment-Plan zur Verwaltung interner Mobilität und Restrukturierung.
              </p>
              <Button onClick={() => setShowCreate(true)} className="rounded-full" data-testid="start-plan-btn">
                Ersten Plan erstellen
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Redeployment;
